import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  V2Modal,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { apiPost } from '../../lib/apiClient.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateTimeInput } from '../../lib/date/JalaliDateTimeInput.js';
import { exactWhen, relativeWhen, wallClockToIso } from '../../lib/date/relativeWhen.js';
import { amountColumnHeader } from '../../lib/finance-display.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import type {
  CustomerActivityItem,
  CustomerDeal,
  CustomerHistoryItem,
  CustomerNoteItem,
  CustomerSection,
} from '../../lib/types/customers.js';
import { DocPanel, StatusBadge, UnavailablePanel, type DocColumn } from './Customer360Parts.js';
import type { AmountDisplayUnit } from '../../lib/finance-display.js';

const fa = (n: number) => n.toLocaleString('fa-IR');

const FORBIDDEN = (
  <StatePanel variant="permission_denied" title="برای مشاهده این بخش دسترسی ندارید" />
);

const DEAL_TONE: Record<CustomerDeal['status'], V2BadgeTone> = {
  new: 'neutral',
  contacted: 'info',
  qualified: 'info',
  converted: 'success',
  disqualified: 'danger',
};

const PRIORITY_TONE: Record<CustomerDeal['priority'], V2BadgeTone> = {
  low: 'neutral',
  normal: 'info',
  high: 'warn',
  urgent: 'danger',
};

/** Shared heading row above a tab's list: title, count and the tab's primary action. */
function TabHead({
  title,
  hint,
  action,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="c360-tabhead">
      <div>
        <h2 className="c360-section__title">{title}</h2>
        {hint ? <p className="c360-note">{hint}</p> : null}
      </div>
      {action}
    </div>
  );
}

/* ───────────────────────────── معاملات (leads) ───────────────────────────── */

export function DealsTab({
  section,
  base,
  customerRef,
  canCreate,
  unit,
  unitLabel,
  formatAmount,
}: {
  readonly section: CustomerSection<CustomerDeal>;
  readonly base: string;
  readonly customerRef: string;
  readonly canCreate: boolean;
  readonly unit: AmountDisplayUnit;
  readonly unitLabel: string;
  readonly formatAmount: (digits: string) => string;
}) {
  if (section.status === 'forbidden') return FORBIDDEN;
  if (section.status === 'unavailable') return <UnavailablePanel tab="deals" base={base} />;
  const deals = section.items ?? [];
  const columns: readonly DocColumn<CustomerDeal>[] = [
    {
      key: 'title',
      header: 'سرنخ',
      cardRole: 'title',
      render: (d) => (
        <Link to={`${base}/crm/leads?layout=list&lead=${encodeURIComponent(d.ref)}`}>
          {d.title}
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      cardRole: 'badge',
      render: (d) => <V2Badge tone={DEAL_TONE[d.status]}>{d.statusLabel}</V2Badge>,
    },
    {
      key: 'stage',
      header: 'مرحله',
      render: (d) =>
        d.open && d.stage
          ? `${d.stage.name} · ${fa(d.probabilityPct)}٪`
          : (d.lostReason?.label ?? '—'),
    },
    {
      key: 'amount',
      header: amountColumnHeader('مبلغ برآوردی', unit),
      align: 'end',
      render: (d) => formatAmount(d.amountRial),
    },
    {
      key: 'close',
      header: 'تاریخ بستن',
      render: (d) => (d.expectedCloseDate ? displayJalaliDate(d.expectedCloseDate) : '—'),
    },
    {
      key: 'priority',
      header: 'اولویت',
      render: (d) => <V2Badge tone={PRIORITY_TONE[d.priority]}>{d.priorityLabel}</V2Badge>,
    },
    { key: 'owner', header: 'مسئول', render: (d) => d.owner ?? '—' },
  ];
  const open = deals.filter((d) => d.open);
  return (
    <div className="c360-stack">
      <TabHead
        title="معاملات"
        hint={`${fa(open.length)} معامله در جریان از ${fa(deals.length)} — سرنخ‌هایی که برای این مشتری ثبت شده یا به آن تبدیل شده‌اند.`}
        action={
          canCreate ? (
            <Link
              className="sk-btn sk-btn--primary"
              to={`${base}/crm/leads?layout=list&new=1&customer=${encodeURIComponent(customerRef)}`}
            >
              معامله جدید
            </Link>
          ) : null
        }
      />
      <DocPanel
        rows={deals}
        rowKey={(d) => d.ref}
        columns={columns}
        searchFields={(d) => [d.ref, d.title, d.statusLabel, d.stage?.name, d.owner]}
        searchPlaceholder="عنوان، مرحله یا مسئول"
        filterOf={(d) => ({ id: d.status, label: d.statusLabel })}
        amountOf={(d) => d.amountRial}
        refOf={(d) => d.ref}
        formatAmount={(d) => `${formatAmount(d)} ${unitLabel}`}
        totalLabel="مجموع مبلغ برآوردی"
        empty={{ icon: 'sales', title: 'هنوز معامله‌ای برای این مشتری ثبت نشده است' }}
      />
    </div>
  );
}

/* ─────────────────────────────── فعالیت‌ها ─────────────────────────────── */

const ACTIVITY_TYPES = [
  ['call', 'تماس'],
  ['meeting', 'جلسه'],
  ['email', 'ایمیل'],
  ['task', 'کار'],
] as const;

const ACTIVITY_TONE: Record<CustomerActivityItem['status'], V2BadgeTone> = {
  open: 'info',
  done: 'success',
  cancelled: 'neutral',
};

export function ActivitiesTab({
  section,
  base,
  customerRef,
  canCreate,
  autoOpen = false,
  onChanged,
}: {
  readonly section: CustomerSection<CustomerActivityItem>;
  readonly base: string;
  readonly customerRef: string;
  readonly canCreate: boolean;
  /** Open the create form on arrival (the «ثبت فعالیت» shortcut). Only honoured with create rights. */
  readonly autoOpen?: boolean;
  readonly onChanged: () => void;
}) {
  const [open, setOpen] = useState(autoOpen && canCreate);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<(typeof ACTIVITY_TYPES)[number][0]>('call');
  const [due, setDue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (section.status === 'forbidden') return FORBIDDEN;
  if (section.status === 'unavailable') return <UnavailablePanel tab="activities" base={base} />;
  const items = section.items ?? [];

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) {
      setError('عنوان فعالیت را بنویسید.');
      return;
    }
    const dueAt = due ? wallClockToIso(due) : null;
    if (due && !dueAt) {
      setError('موعد انتخاب‌شده معتبر نیست.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiPost(`${base}/api/v1/crm/activities`, {
        title: title.trim(),
        activityType: type,
        partyRef: customerRef,
        dueAt: dueAt ?? undefined,
      });
      setOpen(false);
      setTitle('');
      setDue('');
      onChanged();
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت فعالیت انجام نشد. دوباره تلاش کنید.'));
    } finally {
      setBusy(false);
    }
  }

  const columns: readonly DocColumn<CustomerActivityItem>[] = [
    { key: 'title', header: 'فعالیت', cardRole: 'title', render: (a) => a.title },
    {
      key: 'status',
      header: 'وضعیت',
      cardRole: 'badge',
      render: (a) => <V2Badge tone={ACTIVITY_TONE[a.status]}>{a.statusLabel}</V2Badge>,
    },
    {
      key: 'type',
      header: 'نوع',
      render: (a) => <span className="sk-tag">{a.activityTypeLabel}</span>,
    },
    {
      key: 'due',
      header: 'موعد',
      render: (a) =>
        a.dueAt ? <span title={exactWhen(a.dueAt)}>{relativeWhen(a.dueAt)}</span> : 'بدون موعد',
    },
    { key: 'owner', header: 'مسئول', render: (a) => a.owner ?? '—' },
    {
      key: 'lead',
      header: 'ثبت‌شده روی',
      render: (a) =>
        a.leadRef ? (
          <Link to={`${base}/crm/leads?layout=list&lead=${encodeURIComponent(a.leadRef)}`}>
            سرنخ {a.leadRef}
          </Link>
        ) : (
          'خود مشتری'
        ),
    },
  ];

  return (
    <div className="c360-stack">
      <TabHead
        title="فعالیت‌ها و پیگیری‌ها"
        hint="تماس‌ها، جلسه‌ها و کارهای ثبت‌شده برای این مشتری و سرنخ‌های او."
        action={
          canCreate ? (
            <V2Button variant="primary" onClick={() => setOpen(true)}>
              فعالیت جدید
            </V2Button>
          ) : null
        }
      />
      <DocPanel
        rows={items}
        rowKey={(a) => a.ref}
        columns={columns}
        searchFields={(a) => [a.ref, a.title, a.activityTypeLabel, a.owner]}
        searchPlaceholder="عنوان، نوع یا مسئول"
        filterOf={(a) => ({ id: a.status, label: a.statusLabel })}
        refOf={(a) => a.ref}
        empty={{ icon: 'calendar', title: 'فعالیتی برای این مشتری ثبت نشده است' }}
      />
      <V2Modal
        open={open}
        title="فعالیت جدید برای مشتری"
        onClose={() => setOpen(false)}
        dismissible={!busy}
      >
        <form noValidate onSubmit={submit} className="c360-form">
          {error ? <V2Alert tone="danger">{error}</V2Alert> : null}
          <V2FormGrid>
            <V2FormField label="عنوان" required width="wide">
              <input
                className="sk-input"
                value={title}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="نوع" width="short">
              <select
                className="sk-select"
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
              >
                {ACTIVITY_TYPES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="موعد (اختیاری)" width="medium">
              <JalaliDateTimeInput value={due} onChange={setDue} />
            </V2FormField>
          </V2FormGrid>
          <V2FormActions>
            <V2Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'در حال ثبت…' : 'ثبت فعالیت'}
            </V2Button>
            <V2Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              انصراف
            </V2Button>
          </V2FormActions>
        </form>
      </V2Modal>
    </div>
  );
}

/* ─────────────────────────────── یادداشت‌ها ─────────────────────────────── */

export function NotesTab({
  section,
  base,
  customerRef,
  canCreate,
  onChanged,
}: {
  readonly section: CustomerSection<CustomerNoteItem>;
  readonly base: string;
  readonly customerRef: string;
  readonly canCreate: boolean;
  readonly onChanged: () => void;
}) {
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (section.status === 'forbidden') return FORBIDDEN;
  if (section.status === 'unavailable') return <UnavailablePanel tab="notes" base={base} />;
  const notes = section.items ?? [];

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) {
      setError('متن یادداشت را بنویسید.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiPost(`${base}/api/v1/crm/notes`, {
        targetType: 'party',
        targetRef: customerRef,
        body: body.trim(),
      });
      setBody('');
      onChanged();
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت یادداشت انجام نشد. متن شما حفظ شده است.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="c360-stack">
      <TabHead
        title="یادداشت‌ها"
        hint="یادداشت‌های فروش روی این مشتری و روی سرنخ‌های او، از جدید به قدیم."
      />
      {canCreate ? (
        <V2Card>
          <form noValidate onSubmit={submit} className="c360-form c360-section">
            {error ? <V2Alert tone="danger">{error}</V2Alert> : null}
            <V2FormField label="یادداشت جدید" width="full">
              <textarea
                className="sk-textarea"
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </V2FormField>
            <V2FormActions>
              <V2Button type="submit" variant="primary" disabled={busy || !body.trim()}>
                {busy ? 'در حال ثبت…' : 'ثبت یادداشت'}
              </V2Button>
            </V2FormActions>
          </form>
        </V2Card>
      ) : null}
      {notes.length === 0 ? (
        <V2EmptyState icon="info" title="هنوز یادداشتی ثبت نشده است" compact />
      ) : (
        <V2Card>
          <ol className="c360-timeline">
            {notes.map((note) => (
              <li key={note.ref} className="c360-timeline__item">
                <div className="c360-timeline__meta">
                  <strong>{note.author ?? 'عضو سازمان'}</strong>
                  <span title={exactWhen(note.createdAt)}>{relativeWhen(note.createdAt)}</span>
                  {note.source === 'lead' && note.leadRef ? (
                    <Link
                      className="sk-tag"
                      to={`${base}/crm/leads?layout=list&lead=${encodeURIComponent(note.leadRef)}`}
                    >
                      روی سرنخ {note.leadRef}
                    </Link>
                  ) : (
                    <span className="sk-tag">روی مشتری</span>
                  )}
                </div>
                <p className="c360-timeline__body">{note.body}</p>
              </li>
            ))}
          </ol>
        </V2Card>
      )}
    </div>
  );
}

/* ───────────────────────────── تاریخچه تغییرات ───────────────────────────── */

const HISTORY_LABEL: Record<CustomerHistoryItem['kind'], { text: string; tone: V2BadgeTone }> = {
  create: { text: 'ایجاد', tone: 'success' },
  update: { text: 'ویرایش', tone: 'info' },
  archive: { text: 'بایگانی', tone: 'neutral' },
};

export function ChangeHistoryTab({
  section,
  base,
}: {
  readonly section: CustomerSection<CustomerHistoryItem>;
  readonly base: string;
}) {
  if (section.status === 'forbidden') {
    return (
      <StatePanel
        variant="permission_denied"
        title="تاریخچهٔ تغییرات فقط برای دارندگان مجوز «مشاهدهٔ سابقهٔ حسابرسی» نمایش داده می‌شود"
      />
    );
  }
  if (section.status === 'unavailable') return <UnavailablePanel tab="changeHistory" base={base} />;
  const items = section.items ?? [];
  return (
    <div className="c360-stack">
      <TabHead
        title="تاریخچهٔ تغییرات"
        hint="چه کسی و کی پروندهٔ مشتری را تغییر داده است. فقط نام فیلدهای تغییرکرده نمایش داده می‌شود، نه مقدار آن‌ها."
      />
      {items.length === 0 ? (
        <V2EmptyState icon="clock" title="تغییری ثبت نشده است" compact />
      ) : (
        <V2Card>
          <ol className="c360-timeline">
            {items.map((entry) => (
              <li key={entry.id} className="c360-timeline__item">
                <div className="c360-timeline__meta">
                  <StatusBadge
                    label={HISTORY_LABEL[entry.kind].text}
                    tone={
                      HISTORY_LABEL[entry.kind].tone === 'success'
                        ? 'success'
                        : HISTORY_LABEL[entry.kind].tone === 'info'
                          ? 'info'
                          : 'neutral'
                    }
                  />
                  <strong>{entry.actor}</strong>
                  <span title={exactWhen(entry.occurredAt)}>{relativeWhen(entry.occurredAt)}</span>
                </div>
                {entry.changedFields.length > 0 ? (
                  <p className="c360-timeline__body">
                    {entry.kind === 'create' ? 'مقداردهی: ' : 'فیلدهای تغییرکرده: '}
                    {entry.changedFields.map((field) => (
                      <span key={field} className="sk-tag" style={{ marginInlineEnd: 6 }}>
                        {field}
                      </span>
                    ))}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        </V2Card>
      )}
    </div>
  );
}
